"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader, Zap } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ContactInquiry() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  return <section className="relative min-h-screen bg-light-background py-20 px-4 overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="w-full h-full" style={{
        backgroundImage: `
              linear-gradient(to right, hsl(215, 95%, 58%) 1px, transparent 1px),
              linear-gradient(to bottom, hsl(215, 95%, 58%) 1px, transparent 1px)
            `,
        backgroundSize: "40px 40px"
      }} />
      </div>

      <div ref={ref} className={`relative max-w-xl mx-auto transition-all duration-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
        {}
        <div className="flex justify-center mb-8">
          <div className="relative">
            <Zap className="size-12 text-primary animate-pulse" />
            <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full animate-pulse" />
          </div>
        </div>

        <FormProvider className="bg-white/90 backdrop-blur-sm rounded-lg shadow-strong border border-primary/20 p-8" formId="contact-inquiry-services" sectionName="contact-inquiry" defaultValues={{
        name: "",
        email: "",
        organization: "",
        serviceInterest: ""
      }} validationRules={{
        name: {
          required: true,
          minLength: 2,
          maxLength: 50
        },
        email: {
          required: true,
          pattern: "email"
        },
        organization: {
          required: true,
          minLength: 2
        },
        serviceInterest: {
          required: true
        }
      }} submitText="Submit Inquiry" successMessage="Thank you! We'll contact you soon to discuss your automation needs.">
          <ContactForm />
        </FormProvider>
      </div>
    </section>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="text-center py-8 space-y-4">
        <div className="inline-flex items-center justify-center size-16 bg-primary/10 rounded-full mb-4">
          <Zap className="size-8 text-primary" />
        </div>
        <p className="text-foreground-light text-lg font-semibold">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-primary/30 hover:border-primary hover:bg-primary/5">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-6">
        <h2 className="text-3xl font-display font-bold text-foreground-light text-center mb-8">
          Contact Inquiry
        </h2>

        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground-light font-semibold">
                Name
              </FormLabel>
              <FormControl>
                <Input placeholder="Your Name" className="border-border focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/50 transition-all duration-200" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground-light font-semibold">
                Email
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="you@example.com" className="border-border focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/50 transition-all duration-200" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="organization" rules={{
        required: "Organization is required",
        minLength: {
          value: 2,
          message: "Organization must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground-light font-semibold">
                Organization
              </FormLabel>
              <FormControl>
                <Input placeholder="Your Studio or Nonprofit" className="border-border focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/50 transition-all duration-200" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="serviceInterest" rules={{
        required: "Please select a service"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground-light font-semibold">
                Service Interest
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className="w-full border-border focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/50 transition-all duration-200">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="ai-automation">
                    AI-Driven Automation Architecture
                  </SelectItem>
                  <SelectItem value="masterclass">
                    Masterclass Residency Programs
                  </SelectItem>
                  <SelectItem value="both">Both Services</SelectItem>
                  <SelectItem value="consultation">
                    General Consultation
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full bg-primary hover:bg-primary-hover active:bg-primary-active text-foreground-primary font-semibold py-6 text-base shadow-medium hover:shadow-strong hover:scale-[1.02] active:scale-100 transition-all duration-200">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Submitting...
            </> : "Submit Inquiry"}
        </Button>
      </div>
    </Form>;
}